import React, { useState } from 'react'
import {
  TableBody,
} from 'src/components/TableComponents'
import { NumberFmt, measureUnit } from 'src/utils'
import { useTheme } from 'styled-components'
import * as S from '../styles'
import {
  SalesReceivementProduct, OverviewProductStatusEnum, ProductStatusEnum,
} from '../../../ReceivementTypes'
import { ReceivementProductWrapper } from '../../ReceivementProductWrapper'
import notes from 'src/assets/images/other-images/notes.svg'
import check from 'src/assets/images/other-images/check.svg'
import { ProductTableRow } from './ProductTableRow'
import { useObservationModal } from 'src/features/receivement/pages/ReceivementPurchase/hooks/useObservationModal'
import { useSelector } from 'src/utils/store'
import { selectSalesOverview } from 'src/features/receivement/ReceivementSelectors'
import { useDispatch } from 'react-redux'
import { actions } from 'src/features/receivement/ReceivementState'

interface Props {
  product: SalesReceivementProduct
}

const ProductTable = ({ product }: Props) => {
  const [totalAccepted, setTotalAccepted] = React.useState<number>(0)
  const [totalReturnQuantity, setTotalReturnQuantity] = React.useState<number>(0)
  const [totalSwapQuantity, setTotalSwapQuantity] = React.useState<number>(0)
  const theme = useTheme()
  const dispatch = useDispatch()
  const { openObservationModal, ObservationModalComponent } = useObservationModal({ id: product.idItemOrder, productObservation: product.observation })
  const salesOverview = useSelector(selectSalesOverview)
  const batchesState = useSelector(
    (state) => state.receivement.batchesStateByBatchItemOrderIds,
  )
  const [minimumQuantityAccepted, setMinimumQuantityAccepted] = useState(0)
  const [maximumQuantityAccepted, setMaximumQuantityAccepted] = useState(0)
  const [receivementTotal, setReceivementTotal] = useState(0)
  const [acceptsNfdWithoutBreakingBox, setAcceptsNfdWithoutBreakingBox] = useState(false)

  const batchesIds = product.batches.map((batch) => (batch.idsBatchItemOrder.join(',')))

  React.useEffect(() => {
    const keys = Object.keys(batchesState)
    let newTotal = 0

    keys.forEach((key) => {
      if (batchesIds.includes(key)) {
        newTotal += batchesState[key].acceptedQuantity
      }
    })

    setTotalAccepted(newTotal)
  }, [batchesState, batchesIds])

  React.useEffect(() => {
    const keys = Object.keys(batchesState)
    let newtotalReturnQuantity = 0

    keys.forEach((key) => {
      if (batchesIds.includes(key)) {
        newtotalReturnQuantity += batchesState[key].returnQuantity || 0
      }
    })

    setTotalReturnQuantity(newtotalReturnQuantity)
  }, [batchesState, batchesIds])

  React.useEffect(() => {
    const keys = Object.keys(batchesState)
    let newTotalSwap = 0

    keys.forEach((key) => {
      if (batchesIds.includes(key)) {
        newTotalSwap += batchesState[key].swapQuantity || 0
      }
    })

    setTotalSwapQuantity(newTotalSwap)
  }, [batchesState, batchesIds])

  React.useEffect(() => {
    dispatch(actions.setTotalReceivementStockDays({ batchesState }))
  }, [batchesState, dispatch])

  const getDifference = () => {
    const value = product.usefulQuantity - product.expectedQuantity
    const prefix = value > 0 ? '+' : ''
    const color = value > 0 ? theme.colors.confirm : value < 0 ? theme.colors.red : 'inerit'

    return <S.Text style={{ color }}><NumberFmt prefix={prefix} value={value} sufix={`${measureUnit(product.isKg)}`} /></S.Text>
  }

  const returnQuantity = () => {
    const value = product.returnQuantity > 0 ? product.returnQuantity : '-'
    return <S.NumberText isNegative={product.returnQuantity > 0}><NumberFmt prefix='-' value={value} sufix={`${measureUnit(product.isKg)}`} /></S.NumberText>
  }

  React.useEffect(() => {
    setMinimumQuantityAccepted(Math.floor(totalAccepted / product.quantityPerBox) * product.quantityPerBox)
    setMaximumQuantityAccepted(Math.ceil(totalAccepted / product.quantityPerBox) * product.quantityPerBox)
    setReceivementTotal(product.damageQuantity + product.usefulQuantity)
    if (salesOverview) {
      setAcceptsNfdWithoutBreakingBox(!!salesOverview.allowPartialReturn && !salesOverview.allowBreakUnitOnInvoiceReturn)
    }
  }, [product, salesOverview, totalAccepted])

  React.useEffect(() => {
    const quantityAcceptedIsValid = (
      (minimumQuantityAccepted === totalAccepted || maximumQuantityAccepted === totalAccepted) || totalAccepted === 0
    ) && totalAccepted <= receivementTotal

    dispatch(actions.SetQuantityAcceptedIsValid({ modelId: product.modelId, quantityAcceptedIsValid }))
  }, [dispatch, maximumQuantityAccepted, minimumQuantityAccepted, product.modelId, receivementTotal, totalAccepted])

  const getTotalAccepted = () => (
    <>
      {acceptsNfdWithoutBreakingBox ? (
        <>
          {
                ((minimumQuantityAccepted === totalAccepted || maximumQuantityAccepted === totalAccepted)
                || (totalAccepted === 0)) && (totalAccepted <= receivementTotal)
                  ? (
                    <S.TotalAccepted
                      backgroundColor={theme.colors.green}
                      color={theme.colors.darkGray}
                      data-testid='valid-quantity'
                    >
                      <NumberFmt
                        value={totalAccepted}
                        sufix={`${measureUnit(product.isKg)}`}
                      />
                    </S.TotalAccepted>
                  )
                  : (
                    <S.CellTooltip
                      title={totalAccepted > receivementTotal
                        ? `Deve ser aceito no máximo ${receivementTotal}`
                        : `Deve ser aceito ${minimumQuantityAccepted} ou ${maximumQuantityAccepted}`}
                      arrow
                    >
                      <S.TotalAccepted
                        backgroundColor={theme.colors.red}
                        color={theme.colors.white}
                        data-testid='invalid-quantity'
                      >
                        <NumberFmt
                          value={totalAccepted}
                          sufix={`${measureUnit(product.isKg)}`}
                        />
                      </S.TotalAccepted>
                    </S.CellTooltip>
                  )
              }
        </>
      ) : (
        <NumberFmt value={totalAccepted} sufix={`${measureUnit(product.isKg)}`} />
      )}
    </>
  )

  const getTotal = () => {
    const value = product.damageQuantity + product.usefulQuantity
    return <NumberFmt value={value} sufix={`${measureUnit(product.isKg)}`} />
  }

  const status = React.useMemo(() => {
    if (product.status === OverviewProductStatusEnum.CORRECT && product.returnQuantity === 0) {
      return ProductStatusEnum.CORRECT
    }
    return ProductStatusEnum.DIVERGENT
  }, [product.status, product.returnQuantity])

  const getProductInfo = () => `
    ${product.productDescription}
    (Modelo: ${product.productCode},
    Caixaria: ${product.quantityPerBox} ${measureUnit(product.isKg).toLocaleLowerCase()})
  `

  return (
    <ReceivementProductWrapper status={status} buyer={product.buyerName}>
      <S.Table>
        <S.TableHeader
          columnInformation={[
            { columnName: 'AR' },
            { columnName: 'NF' },
            { columnName: 'Produto' },
            { columnName: 'Anotações' },
            { columnName: 'D.E. Atual', tooltipMessage: 'Dias de estoque atual' },
          ]}
          flexColumns={[
            0.05,
            0.05,
            0.8,
            0.1,
            0.1,
          ]}
        />
        <TableBody>
          <S.TableBodyRow backgroundColor={theme.colors.white}>
            <S.TableBodyCell flex={0.05}>{product.idReceivementAuthorization}</S.TableBodyCell>
            <S.TableBodyCell flex={0.05}>{product.invoiceNumber}</S.TableBodyCell>
            <S.TableBodyCell flex={0.8}>{getProductInfo()}</S.TableBodyCell>
            <S.TableBodyCell flex={0.1}>
              <S.Button onClick={() => openObservationModal()}>
                {product.observation
                  && (
                  <S.ImageContainer data-testid='annotation-icon-check'>
                    <S.ImageCheck src={check} />
                  </S.ImageContainer>
                  )}
                <S.Image src={notes} data-testid='annotation-icon'/>
              </S.Button>
            </S.TableBodyCell>
            <S.TableBodyCell flex={0.1}>{product.currentStockDays}</S.TableBodyCell>
          </S.TableBodyRow>
        </TableBody>
        <S.TableHeader
          backgroundColor={theme.colors.gray}
          columnInformation={[
            { columnName: 'Qtd. Recebida' },
            { columnName: 'Qtd. Avariada' },
            { columnName: 'Qtd. Útil' },
            { columnName: 'Data Validade' },
            { columnName: 'Motivo Avaria' },
            { columnName: 'Ação' },
            { columnName: 'Qtd. a Aceitar' },
            { columnName: 'Qtd. a Devolver' },
            { columnName: 'Qtd. a Trocar', hidden: !salesOverview?.allowProductExchange },
            { columnName: 'D.E.' },
          ]}
          flexColumns={[
            1,
            1,
            1,
            1,
            1.5,
            1.5,
            1,
            1.5,
            1,
            1,
          ]}
        />
        <TableBody>
          {product.batches.map((batch) => (
            <ProductTableRow
              key={batch.idsBatchItemOrder.toString()}
              batch={batch}
              product={product}
              productStatus={status}
              allowProductExchange={salesOverview?.allowProductExchange}
            />
          ))}
        </TableBody>
        <S.TableHeader
          backgroundColor={theme.colors.gray}
          columnInformation={[
            { columnName: 'Esperado' },
            { columnName: 'Recebido' },
            { columnName: 'Avariado' },
            { columnName: 'Útil' },
            { columnName: 'Rejeitado AR', tooltipMessage: 'Quantidade a devolver solicitada durante a conferência da nota.', alert: product.returnQuantity > 0 },
            { columnName: '(Útil - Esperado)' },
            { columnName: 'Aceito' },
            { columnName: 'Total a Devolver' },
            { columnName: 'Total a Trocar', hidden: !salesOverview?.allowProductExchange },
            {
              columnName: 'Total D.E.',
              tooltipMessage: 'DE atual + D.E. dos lotes aceitos',
            },
          ]}
          flexColumns={[
            1,
            1,
            1,
            1,
            1.5,
            1.5,
            1,
            1.5,
            1,
            1,
          ]}
        />
        <S.TableBody>
          <S.TableBodyRow backgroundColor={theme.colors.white}>
            <S.TableBodyCell flex={1}>
              <NumberFmt value={product.expectedQuantity} sufix={measureUnit(product.isKg)} />
            </S.TableBodyCell>
            <S.TableBodyCell flex={1}>{getTotal()}</S.TableBodyCell>
            <S.TableBodyCell flex={1}>
              <NumberFmt value={product.damageQuantity} sufix={measureUnit(product.isKg)} />
            </S.TableBodyCell>
            <S.TableBodyCell flex={1}>
              <NumberFmt value={product.usefulQuantity} sufix={measureUnit(product.isKg)} />
            </S.TableBodyCell>
            <S.TableBodyCell flex={1.5}>{returnQuantity()}</S.TableBodyCell>
            <S.TableBodyCell flex={1.5}>{getDifference()}</S.TableBodyCell>
            <S.TableBodyCell flex={1}>{getTotalAccepted()}</S.TableBodyCell>
            <S.TableBodyCell flex={1.5}><NumberFmt value={totalReturnQuantity} sufix={`${measureUnit(product.isKg)}`} /></S.TableBodyCell>
            {
              salesOverview?.allowProductExchange && (
                <S.TableBodyCell flex={1}>
                  <NumberFmt value={totalSwapQuantity} sufix={`${measureUnit(product.isKg)}`} />
                </S.TableBodyCell>
              )
            }
            <S.TableBodyCell flex={1}><S.Text>{`${product.totalArStockDays?.toFixed(1)} / ${product.totalReceivementStockDays?.toFixed(1)}`}</S.Text></S.TableBodyCell>
          </S.TableBodyRow>
        </S.TableBody>
      </S.Table>

      {ObservationModalComponent}
    </ReceivementProductWrapper>
  )
}

export { ProductTable }
